import { useState, useRef } from "react";
import { X, UploadCloud, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AGE_GROUPS } from "@/lib/constants";
import { toast } from "sonner";

const MAX_WORDS = 150;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

function countWords(text) {
  return text.trim().split(/\s+/).filter((w) => w.length > 0).length;
}

export default function AddBookModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    author_name: "",
    author_email: "",
    title: "",
    age_group: "",
    description: "",
    purchase_url: "",
    author_link: "",
    status: "approved",
  });
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef(null);

  if (!open) return null;

  const wordCount = countWords(form.description);
  const overWord = wordCount > MAX_WORDS;

  const handleFile = (f) => {
    setError("");
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/jpg"].includes(f.type)) {
      setError("Cover must be JPG or PNG.");
      return;
    }
    if (f.size > MAX_FILE_SIZE) {
      setError("Cover image exceeds 5MB.");
      return;
    }
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
  };

  const reset = () => {
    setForm({
      author_name: "",
      author_email: "",
      title: "",
      age_group: "",
      description: "",
      purchase_url: "",
      author_link: "",
      status: "approved",
    });
    setFile(null);
    setFilePreview(null);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) {
      setError("Please upload a book cover image.");
      return;
    }
    if (overWord) {
      setError("Description exceeds 150 words.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      fd.append("cover_image", file);
      await api.post("/admin/books", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(
        form.status === "approved"
          ? "Book added and published to the shelf."
          : "Book added to the pending queue."
      );
      reset();
      onCreated?.();
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not add book.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(14,35,46,0.55)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="add-book-modal-overlay"
    >
      <div
        className="relative bg-white border border-[#CFD6DC] rounded-xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="add-book-modal-title"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-[#0E232E]/60 hover:text-[#0E232E] transition-colors z-10"
          data-testid="add-book-close"
        >
          <X size={22} />
        </button>

        <div className="p-6 md:p-8">
          <h2 id="add-book-modal-title" className="font-display text-3xl md:text-4xl font-extrabold text-[#0E232E] leading-tight">
            Add a book manually
          </h2>
          <p className="mt-2 text-sm text-[#2F2E2E] leading-relaxed">
            Books you add go straight to the shelf. Switch to <strong>Pending</strong> if you'd like to queue instead.
          </p>

          <form onSubmit={handleSubmit} className="mt-6 space-y-5" data-testid="add-book-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Author's full name *</label>
                <input required className="field-input" value={form.author_name} onChange={(e) => setForm({ ...form, author_name: e.target.value })} data-testid="add-input-author-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Author's email <span className="text-[#0E232E]/40 normal-case tracking-normal">(optional)</span></label>
                <input type="email" className="field-input" value={form.author_email} onChange={(e) => setForm({ ...form, author_email: e.target.value })} data-testid="add-input-author-email" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Book title *</label>
              <input required className="field-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="add-input-title" />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Age group *</label>
                <select required className="field-input" value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} data-testid="add-input-age-group">
                  <option value="">Select an age group…</option>
                  {AGE_GROUPS.map((g) => (
                    <option key={g.value} value={g.value}>{g.label} ({g.range})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Publish as</label>
                <select className="field-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="add-input-status">
                  <option value="approved">Approved (live on shelf)</option>
                  <option value="pending">Pending review</option>
                </select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-[#0E232E]">Book description *</label>
                <span className={`text-xs tabular-nums ${overWord ? "text-[#B23B28]" : "text-[#0E232E]/60"}`} data-testid="add-word-count">
                  {wordCount} / {MAX_WORDS} words
                </span>
              </div>
              <textarea required rows={4} className="field-input resize-y min-h-[110px]" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="add-input-description" />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Book cover (JPG / PNG, max 5MB) *</label>
              <div
                className="border-2 border-dashed border-[#CFD6DC] rounded-lg p-4 flex items-center gap-4 cursor-pointer hover:border-[#38B6D2] transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="add-dropzone"
              >
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="add-input-cover" />
                {filePreview ? (
                  <img src={filePreview} alt="Cover preview" className="w-20 h-28 object-cover rounded shadow-md" />
                ) : (
                  <div className="w-20 h-28 bg-[#F0F3F5] rounded flex items-center justify-center text-[#38B6D2]">
                    <UploadCloud size={24} />
                  </div>
                )}
                <div className="flex-1 text-sm text-[#2F2E2E]">
                  {file ? (
                    <>
                      <div className="text-[#0E232E] font-semibold">{file.name}</div>
                      <div className="text-xs mt-0.5">Click to replace</div>
                    </>
                  ) : (
                    <>
                      <div className="text-[#0E232E] font-semibold">Click to upload cover</div>
                      <div className="text-xs mt-0.5">JPG or PNG · up to 5MB</div>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Purchase link *</label>
                <input required type="url" placeholder="https://amazon.com/..." className="field-input" value={form.purchase_url} onChange={(e) => setForm({ ...form, purchase_url: e.target.value })} data-testid="add-input-purchase-url" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-[#0E232E] mb-1.5">Author website (optional)</label>
                <input type="url" placeholder="https://..." className="field-input" value={form.author_link} onChange={(e) => setForm({ ...form, author_link: e.target.value })} data-testid="add-input-author-link" />
              </div>
            </div>

            {error && (
              <div role="alert" className="text-sm text-[#B23B28] bg-[#FDEEEA] border border-[#F5BDAE] rounded-md p-3" data-testid="add-book-error">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-md border border-[#CFD6DC] text-[#0E232E] font-semibold hover:bg-[#F0F3F5] transition" data-testid="add-book-cancel">
                Cancel
              </button>
              <button type="submit" disabled={submitting || overWord} className="btn-teal px-6 py-2.5 rounded-md flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-testid="add-book-submit">
                {submitting && <Loader2 className="animate-spin" size={16} />}
                {submitting ? "Adding…" : "Add book"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
